import { useState, useCallback } from 'react'
import { useI18n } from './i18n'
import { useQRCode } from './hooks/useQRCode'
import {
  type QRType,
  type QRFormData,
  type QROptions,
  getDefaultFormData,
  getDefaultOptions,
  isValidUrl,
} from './lib/qrUtils'
import { Header } from './components/Header'
import { ContentTypeTabs } from './components/ContentTypeTabs'
import { InputPanel } from './components/InputPanel'
import { DesignPanel } from './components/DesignPanel'
import { PreviewPanel } from './components/PreviewPanel'
import { DownloadButton } from './components/DownloadButton'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'

export function App() {
  const { t } = useI18n()
  const [type, setType] = useState<QRType>('url')
  const [data, setData] = useState<QRFormData>(getDefaultFormData('url'))
  const [options, setOptions] = useState<QROptions>(getDefaultOptions())
  const [errors, setErrors] = useState<Record<string, string>>({})

  const qr = useQRCode(type, data, options)

  // Real-time URL validation
  const validate = useCallback((nextType: QRType, nextData: QRFormData): Record<string, string> => {
    const nextErrors: Record<string, string> = {}
    if (nextType === 'url') {
      const url = nextData.url?.trim() || ''
      if (url && !isValidUrl(url)) {
        nextErrors.url = t('qr.errors.urlInvalid')
      }
    }
    return nextErrors
  }, [t])

  // Handle type change: reset data, options, errors
  const handleTypeChange = useCallback((nextType: QRType) => {
    setType(nextType)
    const nextData = getDefaultFormData(nextType)
    setData(nextData)
    setErrors(validate(nextType, nextData))
  }, [validate])

  // Handle data change with validation
  const handleDataChange = useCallback((nextData: QRFormData) => {
    setData(nextData)
    setErrors(validate(type, nextData))
  }, [type, validate])

  // Handle options change
  const handleOptionsChange = useCallback((nextOptions: QROptions) => {
    setOptions(nextOptions)
  }, [])

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 w-full max-w-5xl mx-auto px-4 py-6">
        {/* Page title */}
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
          {t('qr.title')}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          {t('qr.description')}
        </p>

        {/* Mobile: stacked layout. Desktop: two-column (55/45) */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left panel: input + design + download (55% on desktop) */}
          <div className="w-full lg:w-[55%] flex flex-col gap-6 order-2 lg:order-1">
            {/* Content type tabs */}
            <ContentTypeTabs activeType={type} onChange={handleTypeChange} />

            {/* Input panel */}
            <section className="bg-card rounded-xl p-5 shadow-sm border border-border">
              <InputPanel
                type={type}
                data={data}
                onChange={handleDataChange}
                errors={errors}
              />
            </section>

            {/* Design panel */}
            <DesignPanel options={options} onChange={handleOptionsChange} />

            {/* Download panel */}
            <DownloadButton
              qrDataUrl={qr.dataUrl}
              svg={qr.svg}
              type={type}
              options={options}
              disabled={!qr.dataUrl || qr.isGenerating}
              content={qr.content}
            />

            {/* Security note */}
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
              <span className="shrink-0">🔒</span>
              <span>{t('qr.security.note')}</span>
            </div>
          </div>

          {/* Right panel: preview (45% on desktop) */}
          <div className="w-full lg:w-[45%] order-1 lg:order-2">
            <PreviewPanel
              dataUrl={qr.dataUrl}

              content={qr.content}
              error={qr.error}
              isGenerating={qr.isGenerating}
              level={options.level}
              type={type}
            />
          </div>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}
